import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { ORG, NAV_LINKS, FOOTER_LINKS, LOGO } from "../data/site";
import { SocialLinks } from "./SocialLinks";
import { useContact } from "../context/SiteSettingsContext";

export const SiteFooter = () => {
  const CONTACT = useContact();
  return (
  <footer className="bg-stone-900 text-stone-200" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-3">
            <img
              src={LOGO}
              alt="Spire Inclusion logo: a colourful circular emblem of stylised people reaching upwards together."
              className="h-12 w-12 object-contain bg-white rounded-xl p-1"
            />
            <span className="font-heading text-xl font-bold text-white">{ORG}</span>
          </div>
          <p className="mt-4 text-base text-stone-400 leading-relaxed max-w-xs">
            Person-centred weekday support helping young adults build skills, confidence and
            connection.
          </p>
          <SocialLinks variant="dark" className="mt-5" />
        </div>

        <nav aria-label="Footer navigation">
          <h2 className="font-heading text-lg font-semibold text-white">Explore</h2>
          <ul className="mt-4 space-y-2">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-base text-stone-300 hover:text-orange-400 rounded"
                  data-testid={`footer-nav-${l.to === "/" ? "home" : l.to.replace(/\//g, "")}-link`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-label="Policies and accessibility">
          <h2 className="font-heading text-lg font-semibold text-white">Information</h2>
          <ul className="mt-4 space-y-2">
            {FOOTER_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-base text-stone-300 hover:text-orange-400 rounded"
                  data-testid={`footer-info-${l.to.replace(/\//g, "")}-link`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <h2 className="font-heading text-lg font-semibold text-white">Get in touch</h2>
          <ul className="mt-4 space-y-3 text-base text-stone-300">
            <li className="flex items-start gap-3">
              <Phone className="h-5 w-5 mt-0.5 text-orange-400 shrink-0" aria-hidden="true" />
              <span>{CONTACT.phone}</span>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="h-5 w-5 mt-0.5 text-orange-400 shrink-0" aria-hidden="true" />
              <span>{CONTACT.email}</span>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="h-5 w-5 mt-0.5 text-orange-400 shrink-0" aria-hidden="true" />
              <span>{CONTACT.address}</span>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="h-5 w-5 mt-0.5 text-orange-400 shrink-0" aria-hidden="true" />
              <span>{CONTACT.hours}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-12 pt-8 border-t border-stone-800 text-sm text-stone-400 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p>© {new Date().getFullYear()} {ORG}. All rights reserved.</p>
        </div>
        <Link
          to="/admin/login"
          className="text-stone-400 hover:text-orange-400 rounded self-start sm:self-auto"
          data-testid="footer-staff-login-link"
        >
          Staff login
        </Link>
      </div>
    </div>
  </footer>
  );
};
